import { useEffect, useState } from 'react';
import type { AdminHealth, HealthStatus } from '@comms/shared';
import { api } from '../lib/api';
import { ErrorText, Spinner, cx } from './ui';

const STATUS: Record<HealthStatus, { icon: string; label: string; tone: string }> = {
  ok: { icon: '✓', label: 'OK', tone: 'text-success' },
  warn: { icon: '!', label: 'Needs attention', tone: 'text-warning' },
  error: { icon: '✕', label: 'Problem', tone: 'text-danger' },
};

const bytes = (n: number) =>
  n >= 1024 ** 3
    ? `${(n / 1024 ** 3).toFixed(1)} GB`
    : n >= 1024 ** 2
      ? `${(n / 1024 ** 2).toFixed(1)} MB`
      : `${Math.round(n / 1024)} KB`;

const uptime = (s: number) =>
  s >= 86400
    ? `${Math.floor(s / 86400)} d ${Math.floor((s % 86400) / 3600)} h`
    : s >= 3600
      ? `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`
      : `${Math.max(1, Math.floor(s / 60))} min`;

/** Admin → Overview: health checks, totals, the last week and what's live. Refreshes every 30 s. */
export function HealthOverview() {
  const [health, setHealth] = useState<AdminHealth | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    const load = () =>
      api<AdminHealth>('GET', '/admin/health').then(
        (h) => (setHealth(h), setError(null)),
        setError,
      );
    void load();
    const t = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(t);
  }, []);

  if (!health) return error ? <ErrorText error={error} /> : <Spinner />;
  const problems = health.checks.filter((c) => c.status !== 'ok');
  return (
    <div className="space-y-6">
      <ErrorText error={error} />
      {problems.length > 0 && (
        <div
          role="alert"
          className={cx(
            'rounded border p-3 text-sm text-text-0',
            health.status === 'error'
              ? 'border-danger bg-danger/10'
              : 'border-warning bg-warning/10',
          )}
        >
          <span className="font-semibold">
            {STATUS[health.status].icon}{' '}
            {problems.length === 1 ? '1 thing needs' : `${problems.length} things need`} attention:
          </span>{' '}
          {problems.map((c) => `${c.label}: ${c.detail}`).join(' · ')}
        </div>
      )}

      <section aria-labelledby="health-checks">
        <h2 id="health-checks" className="mb-2 text-lg font-semibold text-text-0">
          Health
        </h2>
        <ul className="divide-y divide-bg-3 rounded bg-bg-1">
          {health.checks.map((c) => (
            <li
              key={c.id}
              data-check={c.id}
              className="flex items-center gap-3 px-4 py-2.5 text-sm"
            >
              <span aria-hidden className={cx('w-4 text-center font-bold', STATUS[c.status].tone)}>
                {STATUS[c.status].icon}
              </span>
              <span className="w-36 shrink-0 font-medium text-text-0">{c.label}</span>
              <span className="min-w-0 flex-1 text-text-2">{c.detail}</span>
              <span className="shrink-0 text-xs text-text-3">{STATUS[c.status].label}</span>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-text-3">
          Version {health.version} · up {uptime(health.uptimeSeconds)} · memory{' '}
          {bytes(health.process.rssBytes)} · CPU {health.process.cpuPercent}%
        </p>
      </section>

      <section aria-labelledby="health-live">
        <h2 id="health-live" className="mb-2 text-lg font-semibold text-text-0">
          Right now
        </h2>
        <Tiles
          items={[
            ['Connected', health.live.gatewayConnections],
            ['In voice', health.live.voiceParticipants],
            ['Voice rooms & calls', health.live.voiceRooms],
          ]}
        />
      </section>

      <section aria-labelledby="health-week">
        <h2 id="health-week" className="mb-2 text-lg font-semibold text-text-0">
          Messages in server channels, last 7 days
        </h2>
        <ActivityChart activity={health.activity} />
        <p className="mt-2 text-xs text-text-3">
          Encrypted DMs aren't counted per day. New accounts this week:{' '}
          {health.activity.reduce((n, d) => n + d.signups, 0)}
        </p>
      </section>

      <section aria-labelledby="health-totals">
        <h2 id="health-totals" className="mb-2 text-lg font-semibold text-text-0">
          Totals
        </h2>
        <Tiles
          items={[
            ['People', health.counts.users],
            ['Servers', health.counts.guilds],
            ['Messages', health.counts.messages],
            ['Encrypted messages', health.counts.encryptedMessages],
            ['Files', health.counts.attachments],
            ['File storage', bytes(health.counts.storageBytes)],
          ]}
        />
      </section>
    </div>
  );
}

function Tiles({ items }: { items: [string, number | string][] }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {items.map(([label, value]) => (
        <div key={label} className="rounded bg-bg-1 p-4">
          <div className="text-xs uppercase tracking-wide text-text-3">{label}</div>
          <div className="text-2xl font-semibold tabular-nums text-text-0">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </div>
        </div>
      ))}
    </div>
  );
}

/** One series (messages per day): columns from a shared baseline, hover for the exact value. */
function ActivityChart({ activity }: { activity: AdminHealth['activity'] }) {
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  const max = Math.max(1, ...activity.map((d) => d.messages));
  const day = (date: string) =>
    new Date(`${date}T12:00:00Z`).toLocaleDateString([], { weekday: 'short' });
  return (
    <div className="rounded bg-bg-1 p-4">
      {activity.every((d) => d.messages === 0) && !table ? (
        <p className="flex h-36 items-center justify-center text-sm text-text-3">
          No messages in server channels this week yet.
        </p>
      ) : table ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-text-3">
              <th className="font-medium">Day</th>
              <th className="text-right font-medium">Messages</th>
              <th className="text-right font-medium">New accounts</th>
            </tr>
          </thead>
          <tbody>
            {activity.map((d) => (
              <tr key={d.date} className="text-text-1">
                <td>{d.date}</td>
                <td className="text-right tabular-nums">{d.messages.toLocaleString()}</td>
                <td className="text-right tabular-nums">{d.signups}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div
          role="img"
          aria-label={`Messages per day: ${activity.map((d) => `${day(d.date)} ${d.messages}`).join(', ')}`}
          className="relative flex h-36 items-end gap-0.5 border-b border-bg-3"
          onMouseLeave={() => setHover(null)}
        >
          {activity.map((d, i) => (
            <div
              key={d.date}
              className="relative flex h-full flex-1 items-end justify-center"
              onMouseEnter={() => setHover(i)}
            >
              <div
                className={cx(
                  'w-full max-w-6 rounded-t bg-accent',
                  hover !== null && hover !== i && 'opacity-60',
                )}
                style={{ height: `${Math.max(d.messages ? 2 : 0, (d.messages / max) * 100)}%` }}
              />
              {hover === i && (
                <div className="pointer-events-none absolute bottom-full mb-1 whitespace-nowrap rounded bg-bg-0 px-2 py-1 text-xs text-text-0 shadow">
                  {d.date}: {d.messages.toLocaleString()} messages, {d.signups} new
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {!table && activity.some((d) => d.messages > 0) && (
        <div className="mt-1 flex gap-0.5 text-center text-[11px] text-text-3">
          {activity.map((d) => (
            <span key={d.date} className="flex-1">
              {day(d.date)}
            </span>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={() => setTable(!table)}
        className="mt-2 text-xs text-accent hover:underline"
      >
        {table ? 'Show chart' : 'Show as table'}
      </button>
    </div>
  );
}
